import { Injectable } from '@angular/core';
import {
  IMetricasGateways,
  IMetricaItem,
  IMetricasGateway,
  ILogEvento,
} from 'modelos/src';
import * as Highcharts from 'highcharts';
import {
  ILineChartData,
  IHeatmapChartData,
  IScatterChartData,
} from '../../../../../auxiliares/graficos/graficos.component';
import { ChartColors } from '../../../../../auxiliares/servicios/chart-colors.service';

/**
 * Servicio para procesar métricas de gateways y generar datos de gráficos
 */
@Injectable({
  providedIn: 'root',
})
export class GatewayMetricasService {
  constructor() {}

  //Procesa métricas de RX (recepción) para generar datos de gráfico de línea
  //@param metricas Métricas del gateway
  //@returns Datos de gráfico de línea con timestamps en eje X

  public procesarDatosRxPackets(metricas: IMetricasGateways): {
    datos: ILineChartData[];
    categorias: string[];
    timestamps: string[];
  } {
    return this.procesarMetricaGeneral(
      metricas.rxPackets,
      'Paquetes Recibidos',
      ChartColors.success(),
    );
  }
  //Procesa métricas de TX (transmisión) para generar datos de gráfico de línea
  //@param metricas Métricas del gateway
  //@returns Datos de gráfico de línea con timestamps en eje X

  public procesarDatosTxPackets(metricas: IMetricasGateways): {
    datos: ILineChartData[];
    categorias: string[];
    timestamps: string[];
  } {
    return this.procesarMetricaGeneral(
      metricas.txPackets,
      'Paquetes Transmitidos',
      ChartColors.info(),
    );
  }

  //Método privado que procesa una métrica genérica
  // @param metrica Métrica a procesar (rxPackets o txPackets)
  // @param nombre Nombre de la serie
  // @param color Color de la línea
  // @returns Objeto con datos del gráfico, categorías (timestamps formateados) y timestamps originales
  private procesarMetricaGeneral(
    metrica: IMetricaItem,
    nombre: string,
    color: string,
  ): {
    datos: ILineChartData[];
    categorias: string[];
    timestamps: string[];
  } {
    // Validar que existan datos
    if (!metrica || !metrica.datasets || metrica.datasets.length === 0) {
      return {
        datos: [],
        categorias: [],
        timestamps: [],
      };
    }

    // Obtener el dataset principal (primer elemento del array)
    const dataset = metrica.datasets[0];

    if (!dataset || !dataset.data || dataset.data.length === 0) {
      return {
        datos: [],
        categorias: [],
        timestamps: [],
      };
    }

    // Extraer timestamps originales y formateados
    const categoriasFormateadas = this.formatearTimestamps(metrica.timestamps);
    const timestampsOriginales = metrica.timestamps;

    // Enriquecer datos con información de tooltip
    const datosEnriquecidos = dataset.data.map((valor, index) => ({
      y: valor,
      timestamp: timestampsOriginales[index], // Para usar en tooltip personalizado
    }));

    // Crear datos del gráfico
    const datos: ILineChartData[] = [
      {
        name: nombre,
        data: datosEnriquecidos as any, // Passar objetos en lugar de números simples
        color: color,
      },
    ];

    return {
      datos,
      categorias: categoriasFormateadas,
      timestamps: timestampsOriginales,
    };
  }

  //Formatea una lista de timestamps ISO a formato legible (solo día/mes)
  // @param timestamps Array de timestamps en formato ISO
  // @returns Array de strings formateados

  private formatearTimestamps(timestamps: string[]): string[] {
    if (!timestamps || timestamps.length === 0) {
      return [];
    }

    return timestamps.map((timestamp) => {
      try {
        const date = new Date(timestamp);
        const dia = date.getDate().toString().padStart(2, '0');
        const mes = (date.getMonth() + 1).toString().padStart(2, '0');

        // Formato: dd/mm
        return `${dia}/${mes}`;
      } catch (e) {
        return timestamp; // Retornar el timestamp original si hay error
      }
    });
  }

  //Procesa métricas de RX por Frecuencia para generar datos de heatmap
  //@param metricas Métricas del gateway
  // @returns Datos del heatmap con fechas en X, frecuencias en Y, conteo en color

  public procesarRxPacketsPerFreq(metricas: IMetricasGateways): {
    heatmapData: IHeatmapChartData;
    categoriasX: string[];
    categoriasY: string[];
  } {
    return this.procesarHeatmapFrequency(metricas.rxPacketsPerFreq);
  }

  //Procesa métricas de TX por Frecuencia para generar datos de heatmap
  //@param metricas Métricas del gateway
  // @returns Datos del heatmap con fechas en X, frecuencias en Y, conteo en color

  public procesarTxPacketsPerFreq(metricas: IMetricasGateways): {
    heatmapData: IHeatmapChartData;
    categoriasX: string[];
    categoriasY: string[];
  } {
    return this.procesarHeatmapFrequency(metricas.txPacketsPerFreq);
  }

  //Procesa métricas de RX por Data Rate para generar datos de heatmap
  //@param metricas Métricas del gateway
  //@returns Datos del heatmap con fechas en X, data rates en Y, conteo en color

  public procesarRxPacketsPerDr(metricas: IMetricasGateways): {
    heatmapData: IHeatmapChartData;
    categoriasX: string[];
    categoriasY: string[];
  } {
    return this.procesarHeatmapDataRate(metricas.rxPacketsPerDr);
  }

  //Procesa métricas de TX por Data Rate para generar datos de heatmap
  //@param metricas Métricas del gateway
  //@returns Datos del heatmap con fechas en X, data rates en Y, conteo en color

  public procesarTxPacketsPerDr(metricas: IMetricasGateways): {
    heatmapData: IHeatmapChartData;
    categoriasX: string[];
    categoriasY: string[];
  } {
    return this.procesarHeatmapDataRate(metricas.txPacketsPerDr);
  }

  //Procesa datos de heatmap por frecuencia
  //@param metrica Métrica del gateway
  //@returns Objeto con datos heatmap, categorías X (fechas) y categorías Y (frecuencias en MHz)

  private procesarHeatmapFrequency(metrica: IMetricaItem): {
    heatmapData: IHeatmapChartData;
    categoriasX: string[];
    categoriasY: string[];
  } {
    if (!metrica || !metrica.datasets || metrica.datasets.length === 0) {
      return {
        heatmapData: { data: [] },
        categoriasX: [],
        categoriasY: [],
      };
    }

    // Crear array con índice original, valor numérico y label formateado
    const frecuenciasConIndice = metrica.datasets.map((dataset, index) => {
      const freqLabel = dataset.label;
      const freqValue = parseFloat(freqLabel);
      let labelFormateado = freqLabel;

      if (!isNaN(freqValue)) {
        if (freqValue > 1000000) {
          labelFormateado = `${(freqValue / 1000000).toLocaleString('es-AR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} MHz`;
        } else if (freqValue > 1000) {
          labelFormateado = `${(freqValue / 1000).toLocaleString('es-AR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} MHz`;
        } else {
          labelFormateado = `${freqValue.toLocaleString('es-AR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} MHz`;
        }
      }

      return {
        indiceOriginal: index,
        valorNumerico: freqValue || 0,
        label: labelFormateado,
      };
    });

    // Ordenar por valor numérico de menor a mayor
    frecuenciasConIndice.sort((a, b) => a.valorNumerico - b.valorNumerico);

    // Crear mapa de índice original a nuevo índice
    const mapaIndices = new Map<number, number>();
    frecuenciasConIndice.forEach((item, nuevoIndice) => {
      mapaIndices.set(item.indiceOriginal, nuevoIndice);
    });

    // Obtener categorías Y ordenadas
    const categoriasY = frecuenciasConIndice.map((item) => item.label);

    // Obtener categorías X (fechas formateadas)
    const categoriasX = this.formatearTimestamps(metrica.timestamps);

    // Construir datos del heatmap con índices remapeados
    const datosHeatmap: [number, number, number][] = [];

    metrica.datasets.forEach((dataset, yIndexOriginal) => {
      const yIndexNuevo = mapaIndices.get(yIndexOriginal) ?? yIndexOriginal;
      if (dataset.data && dataset.data.length > 0) {
        dataset.data.forEach((valor, xIndex) => {
          datosHeatmap.push([xIndex, yIndexNuevo, valor]);
        });
      }
    });

    return {
      heatmapData: { data: datosHeatmap, name: 'Conteo' },
      categoriasX,
      categoriasY,
    };
  }

  //Procesa datos de heatmap por data rate
  //@param metrica Métrica del gateway
  //@returns Objeto con datos heatmap, categorías X (fechas) y categorías Y (data rates)

  private procesarHeatmapDataRate(metrica: IMetricaItem): {
    heatmapData: IHeatmapChartData;
    categoriasX: string[];
    categoriasY: string[];
  } {
    if (!metrica || !metrica.datasets || metrica.datasets.length === 0) {
      return {
        heatmapData: { data: [] },
        categoriasX: [],
        categoriasY: [],
      };
    }

    // Crear array con índice original y valor numérico para ordenar
    const dataRatesConIndice = metrica.datasets.map((dataset, index) => {
      const label = dataset.label;
      // Extraer número del label (ej: "DR0" -> 0, "5" -> 5)
      const match = label.match(/\d+/);
      const valorNumerico = match ? parseInt(match[0], 10) : 0;

      return {
        indiceOriginal: index,
        valorNumerico,
        label,
      };
    });

    // Ordenar por valor numérico de menor a mayor (DR0, DR1, DR2...)
    dataRatesConIndice.sort((a, b) => a.valorNumerico - b.valorNumerico);

    // Crear mapa de índice original a nuevo índice
    const mapaIndices = new Map<number, number>();
    dataRatesConIndice.forEach((item, nuevoIndice) => {
      mapaIndices.set(item.indiceOriginal, nuevoIndice);
    });

    // Obtener categorías Y ordenadas
    const categoriasY = dataRatesConIndice.map((item) => item.label);

    // Obtener categorías X (fechas formateadas)
    const categoriasX = this.formatearTimestamps(metrica.timestamps);

    // Construir datos del heatmap con índices remapeados
    const datosHeatmap: [number, number, number][] = [];

    metrica.datasets.forEach((dataset, yIndexOriginal) => {
      const yIndexNuevo = mapaIndices.get(yIndexOriginal) ?? yIndexOriginal;
      if (dataset.data && dataset.data.length > 0) {
        dataset.data.forEach((valor, xIndex) => {
          datosHeatmap.push([xIndex, yIndexNuevo, valor]);
        });
      }
    });

    return {
      heatmapData: { data: datosHeatmap, name: 'Conteo' },
      categoriasX,
      categoriasY,
    };
  }

  //Procesa métricas de TX por Estado para generar datos de gráfico de barras
  //@param metricas Métricas del gateway
  //@returns Objeto con series de datos, categorías X (fechas) y paleta de colores por estado

  public procesarTxPacketsPerStatus(metricas: IMetricasGateways): {
    series: { name: string; data: number[]; color: string }[];
    categorias: string[];
    colores: string[];
  } {
    const metrica = metricas.txPacketsPerStatus;

    if (!metrica || !metrica.datasets || metrica.datasets.length === 0) {
      return {
        series: [],
        categorias: [],
        colores: [],
      };
    }

    // Obtener categorías X (fechas formateadas)
    const categorias = this.formatearTimestamps(metrica.timestamps);

    // Paleta de colores diversa
    const paletaColores = [
      '#FF6B6B', // Rojo
      '#4ECDC4', // Turquesa
      '#45B7D1', // Azul claro
      '#FFA07A', // Salmón
      '#98D8C8', // Verde menta
      '#F7DC6F', // Amarillo
      '#BB8FCE', // Púrpura
      '#85C1E2', // Azul
      '#F8B88B', // Naranja claro
      '#82E0AA', // Verde
      '#F5B7B1', // Rosa
      '#AED6F1', // Celeste
    ];

    // Crear series con colores únicos para cada estado
    const series = metrica.datasets.map((dataset, index) => {
      const estado = dataset.label;
      const color = paletaColores[index % paletaColores.length]; // Ciclar si hay más de 12 estados
      return {
        name: estado,
        data: dataset.data.map((valor) => ({
          y: valor,
          seriesColor: color, // Pasar color a cada punto para usarlo en tooltip
        })) as any,
        color: color,
      };
    });

    // Extraer los colores únicos para usar en el gráfico
    const colores = series.map((s) => s.color);

    return {
      series,
      categorias,
      colores,
    };
  }

  /**
   * Retorna opciones personalizadas para tooltip de gráficos lineales (RX/TX Packets)
   */
  public getOpcionesTooltipLineChart(): Partial<Highcharts.Options> {
    return {
      tooltip: {
        headerFormat: '',
        borderRadius: 4,
        shadow: false,
        formatter: function (this: any) {
          if (this.point && this.point.timestamp) {
            // Si el punto tiene timestamp personalizado
            const fecha = new Date(this.point.timestamp);
            const dia = fecha.getDate().toString().padStart(2, '0');
            const mes = (fecha.getMonth() + 1).toString().padStart(2, '0');
            const valor = this.point.y;
            const color = this.series.color || '#2196F3'; // Usa el color de la serie
            return `<span style="font-size: 12px; color: #666;">Fecha:</span> <b style="font-size: 13px; color: #333;">${dia}/${mes}/${fecha.getFullYear()}</b><br/><span style="font-size: 12px; color: #666;">Conteo:</span> <b style="font-size: 13px; color: ${color};">${valor}</b>`;
          } else {
            // Fallback para datos simples
            const valor = this.point.y;
            return `<b>${valor}</b>`;
          }
        },
      },
    };
  }

  /**
   * Retorna opciones personalizadas para tooltips de heatmaps
   */
  public getOpcionesTooltipHeatmap(): Partial<Highcharts.Options> {
    return {
      tooltip: {
        headerFormat: '',
        borderRadius: 6,
        shadow: true,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        borderColor: '#333',
        borderWidth: 1,
        padding: 12,
        formatter: function (this: any) {
          const xCategory = this.series.xAxis.categories[this.point.x] || 'N/A';
          const yCategory = this.series.yAxis.categories[this.point.y] || 'N/A';
          const conteo = this.point.value;
          const colorHex = this.color || '#0066cc';

          return `<div style="min-width: 200px;">
            <div style="margin-bottom: 8px; font-weight: bold; font-size: 13px; color: #fff;">Detalles</div>
            <div style="display: flex; align-items: center; margin-bottom: 6px;">
              <span style="display: inline-block; width: 10px; height: 10px; background-color: ${colorHex}; border-radius: 2px; margin-right: 8px;"></span>
              <span style="font-size: 12px; color: #ccc;">Fecha:</span>
              <span style="font-size: 12px; color: #fff; margin-left: 6px; font-weight: 600;">${xCategory}</span>
            </div>
            <div style="display: flex; align-items: center; margin-bottom: 6px;">
              <span style="display: inline-block; width: 10px; height: 10px; background-color: ${colorHex}; border-radius: 2px; margin-right: 8px;"></span>
              <span style="font-size: 12px; color: #ccc;">Categoría:</span>
              <span style="font-size: 12px; color: #fff; margin-left: 6px; font-weight: 600;">${yCategory}</span>
            </div>
            <div style="display: flex; align-items: center; padding-top: 8px; border-top: 1px solid rgba(255, 255, 255, 0.2);">
              <span style="display: inline-block; width: 10px; height: 10px; background-color: ${colorHex}; border-radius: 2px; margin-right: 8px;"></span>
              <span style="font-size: 12px; color: #ccc;">Conteo:</span>
              <span style="font-size: 14px; color: ${colorHex}; margin-left: 6px; font-weight: bold;">${conteo}</span>
            </div>
          </div>`;
        },
      },
    };
  }

  /**
   * Retorna opciones personalizadas para tooltip del gráfico de barras de TX por Estado
   */
  public getOpcionesTooltipTxStatus(): Partial<Highcharts.Options> {
    return {
      tooltip: {
        headerFormat: '',
        borderRadius: 6,
        shadow: true,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        borderColor: '#333',
        borderWidth: 1,
        padding: 12,
        formatter: function (this: any) {
          const fecha = this.x;
          const estado = this.series.name;
          const conteo = this.y;
          // Usar el color de la serie o el del punto
          const colorHex =
            this.point.seriesColor || this.series.color || '#9C27B0';

          return `<div style="min-width: 200px;">
            <div style="margin-bottom: 8px; font-weight: bold; font-size: 13px; color: #fff;">Detalles</div>
            <div style="display: flex; align-items: center; margin-bottom: 6px;">
              <span style="font-size: 12px; color: #ccc;">Fecha:</span>
              <span style="font-size: 12px; color: #fff; margin-left: 6px; font-weight: 600;">${fecha}</span>
            </div>
            <div style="display: flex; align-items: center; margin-bottom: 6px;">
              <span style="font-size: 12px; color: #ccc;">Estado:</span>
              <span style="font-size: 12px; font-weight: bold; margin-left: 6px; color: ${colorHex};">${estado}</span>
            </div>
            <div style="display: flex; align-items: center; padding-top: 8px; border-top: 1px solid rgba(255, 255, 255, 0.2);">
              <span style="font-size: 12px; color: #ccc;">Conteo:</span>
              <span style="font-size: 14px; margin-left: 6px; font-weight: bold; color: ${colorHex}">${conteo}</span>
            </div>
          </div>`;
        },
      },
    };
  }

  /**
   * Procesa métricas de Airtime (Time-on-Air) para generar datos de heatmap
   * @param datos Array de IMetricasGateway
   * @returns Datos del heatmap con horas en X, canales en Y, ToA en color
   */
  public procesarAirtimeHeatmap(datos: IMetricasGateway[]): {
    heatmapData: IHeatmapChartData;
    categoriasX: string[];
    categoriasY: string[];
  } {
    if (!datos || datos.length === 0) {
      return {
        heatmapData: { data: [] },
        categoriasX: [],
        categoriasY: [],
      };
    }

    // Extraer canales únicos y ordenarlos
    const canalesSet = new Set<number>();
    datos.forEach((d) => canalesSet.add(d.canal));
    const canales = Array.from(canalesSet).sort((a, b) => a - b);

    // Generar las últimas 24 horas hacia atrás desde la hora actual
    const ahora = new Date();
    const horaActual = ahora.getHours();
    const horas: string[] = [];

    // Generar 24 horas hacia atrás desde la hora actual (redondeada)
    // Empezar desde 23 horas atrás hasta la hora actual
    for (let i = 23; i >= 0; i--) {
      const hora = (horaActual - i + 24) % 24;
      horas.push(hora.toString().padStart(2, '0') + ':00');
    }

    // Crear mapa para acceso rápido: clave = "hora|canal"
    const dataMap = new Map<string, number>();
    datos.forEach((d) => {
      const fecha = new Date(d.fecha);
      const hora = fecha.getHours().toString().padStart(2, '0') + ':00';
      const key = `${hora}|${d.canal}`;
      // Acumular si hay múltiples entradas para la misma hora/canal
      const existing = dataMap.get(key) || 0;
      dataMap.set(key, existing + d.totalToA);
    });

    // Construir datos del heatmap: [xIndex, yIndex, value]
    const datosHeatmap: [number, number, number][] = [];
    horas.forEach((hora, xIndex) => {
      canales.forEach((canal, yIndex) => {
        const key = `${hora}|${canal}`;
        const valor = dataMap.get(key) || 0;
        // Convertir ms a segundos para mejor legibilidad
        datosHeatmap.push([
          xIndex,
          yIndex,
          Math.round((valor / 1000) * 100) / 100,
        ]);
      });
    });

    // Formatear canales a MHz
    const categoriasY = canales.map((canal) => {
      const freqMhz = (canal / 1000000).toLocaleString('es-AR', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      });
      return `${freqMhz} MHz`;
    });

    return {
      heatmapData: { data: datosHeatmap, name: 'ToA (seg)' },
      categoriasX: horas,
      categoriasY,
    };
  }

  /**
   * Retorna opciones personalizadas para tooltip del heatmap de Airtime
   * Escala de colores basada en ocupación del canal:
   * - Verde (0-180 seg): 0-5% ocupación - Óptimo
   * - Amarillo (180-600 seg): 5-17% ocupación - Moderado
   * - Naranja (600-1200 seg): 17-33% ocupación - Alto
   * - Rojo (>1200 seg): >33% ocupación - Saturación
   */
  public getOpcionesTooltipAirtime(): Partial<Highcharts.Options> {
    return {
      colorAxis: {
        min: 0,
        max: 1200, // 20 minutos = 33% ocupación
        stops: [
          [0, '#4CAF50'], // Verde - 0 seg
          [0.15, '#8BC34A'], // Verde claro - 180 seg (5%)
          [0.5, '#FFC107'], // Amarillo - 600 seg (17%)
          [0.85, '#FF9800'], // Naranja - 1020 seg (28%)
          [1, '#F44336'], // Rojo - 1200 seg (33%)
        ],
      },
      tooltip: {
        headerFormat: '',
        borderRadius: 6,
        shadow: true,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        borderColor: '#333',
        borderWidth: 1,
        padding: 12,
        formatter: function (this: any) {
          const hora = this.series.xAxis.categories[this.point.x] || 'N/A';
          const canal = this.series.yAxis.categories[this.point.y] || 'N/A';
          const toaSegundos = this.point.value;
          const ocupacion = ((toaSegundos / 3600) * 100).toLocaleString(
            'es-AR',
            { minimumFractionDigits: 2, maximumFractionDigits: 2 },
          );
          const colorHex = this.color || '#FF6B35';

          // Determinar estado según ocupación
          let estado = 'Óptimo';
          if (toaSegundos > 1200) estado = 'Saturación';
          else if (toaSegundos > 600) estado = 'Alto';
          else if (toaSegundos > 180) estado = 'Moderado';

          return `<div style="min-width: 220px;">
            <div style="margin-bottom: 8px; font-weight: bold; font-size: 13px; color: #fff;">Time-on-Air</div>
            <div style="display: flex; align-items: center; margin-bottom: 6px;">
              <span style="font-size: 12px; color: #ccc;">Hora:</span>
              <span style="font-size: 12px; color: #fff; margin-left: 6px; font-weight: 600;">${hora}</span>
            </div>
            <div style="display: flex; align-items: center; margin-bottom: 6px;">
              <span style="font-size: 12px; color: #ccc;">Canal:</span>
              <span style="font-size: 12px; color: #fff; margin-left: 6px; font-weight: 600;">${canal}</span>
            </div>
            <div style="display: flex; align-items: center; margin-bottom: 6px;">
              <span style="font-size: 12px; color: #ccc;">ToA:</span>
              <span style="font-size: 14px; color: ${colorHex}; margin-left: 6px; font-weight: bold;">${toaSegundos} seg</span>
            </div>
            <div style="display: flex; align-items: center; margin-bottom: 6px;">
              <span style="font-size: 12px; color: #ccc;">Ocupación:</span>
              <span style="font-size: 12px; color: #fff; margin-left: 6px; font-weight: 600;">${ocupacion} %</span>
            </div>
            <div style="display: flex; align-items: center; padding-top: 8px; border-top: 1px solid rgba(255, 255, 255, 0.2);">
              <span style="font-size: 12px; color: #ccc;">Estado:</span>
              <span style="font-size: 12px; color: ${colorHex}; margin-left: 6px; font-weight: bold;">${estado}</span>
            </div>
          </div>`;
        },
      },
    };
  }

  /**
   * Procesa uplinks para generar scatter plot
   * @param uplinks Array de uplinks del gateway (ILogEvento[])
   * @param gatewayEui EUI del gateway para filtrar rxInfo
   * @returns Datos scatter con configuración de gradiente
   */
  public procesarUplinkScatter(
    uplinks: ILogEvento[],
    gatewayEui: string,
  ): {
    scatterData: IScatterChartData[];
    colorAxisMin: number;
    colorAxisMax: number;
    colorAxisStops: [number, string][];
    minRssi: number;
    maxRssi: number;
  } {
    if (!uplinks || uplinks.length === 0) {
      return {
        scatterData: [],
        colorAxisMin: 0,
        colorAxisMax: 1000,
        colorAxisStops: [],
        minRssi: -130,
        maxRssi: -70,
      };
    }

    // Transformar a formato scatter: {x: timestamp_ms, y: rssi, z: toa}
    const data: { x: number; y: number; z: number }[] = [];

    uplinks.forEach((uplink) => {
      if (!uplink.metadatosUplink?.rxInfo) return;

      // Buscar el rxInfo correspondiente a este gateway (case-insensitive)
      const rxInfo = uplink.metadatosUplink.rxInfo.find(
        (rx) => rx.gatewayId.toLowerCase() === gatewayEui.toLowerCase(),
      );

      if (!rxInfo) return;

      // Usar nsTime si está disponible, sino fechaCreacion
      const timestamp = new Date(
        rxInfo.nsTime || uplink.fechaCreacion || '',
      ).getTime();
      const rssi = rxInfo.rssi || 0;
      const toa = uplink.metadatosUplink.timeOnAir || 0;

      if (timestamp && rssi) {
        data.push({ x: timestamp, y: rssi, z: toa });
      }
    });

    // Calcular rango de RSSI
    const rssiValues = data.map((d) => d.y);
    const minRssi = Math.floor(Math.min(...rssiValues, -130) / 10) * 10;
    const maxRssi = Math.ceil(Math.max(...rssiValues, -70) / 10) * 10;

    // Calcular rango de ToA para escala de color
    const toaValues = data.map((d) => d.z);
    const maxToa = Math.max(...toaValues, 1000);
    const minToa = Math.min(...toaValues, 0);

    // Definir stops de color basados en ocupación de canal
    // 0-200ms: verde (bajo)
    // 200-500ms: amarillo (moderado)
    // 500-1000ms: naranja (alto)
    // >1000ms: rojo (crítico)
    const colorAxisStops: [number, string][] = [
      [0, '#4CAF50'], // Verde
      [0.2, '#8BC34A'], // Verde claro
      [0.5, '#FFC107'], // Amarillo
      [0.75, '#FF9800'], // Naranja
      [1, '#F44336'], // Rojo
    ];

    const scatterData: IScatterChartData[] = [
      {
        name: 'Uplinks',
        data: data,
      },
    ];

    return {
      scatterData,
      colorAxisMin: minToa,
      colorAxisMax: Math.max(maxToa, 1000),
      colorAxisStops,
      minRssi,
      maxRssi,
    };
  }

  /**
   * Retorna opciones personalizadas para tooltip del scatter de uplinks
   */
  public getOpcionesTooltipUplinks(): Partial<Highcharts.Options> {
    return {
      tooltip: {
        headerFormat: '',
        borderRadius: 6,
        shadow: true,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        borderColor: '#333',
        borderWidth: 1,
        padding: 12,
        useHTML: true,
        formatter: function (this: any) {
          const point = this.point;
          const timestamp = new Date(point.x);
          const rssi = point.y;
          const toa = point.z || 0;
          const colorHex = this.color || '#2196F3';

          // Formatear timestamp
          const dia = timestamp.getDate().toString().padStart(2, '0');
          const mes = (timestamp.getMonth() + 1).toString().padStart(2, '0');
          const hora = timestamp.getHours().toString().padStart(2, '0');
          const min = timestamp.getMinutes().toString().padStart(2, '0');
          const seg = timestamp.getSeconds().toString().padStart(2, '0');
          const fechaStr = `${dia}/${mes}/${timestamp.getFullYear()} ${hora}:${min}:${seg}`;

          // Determinar calidad de señal
          let calidadSenal = 'Excelente';
          if (rssi < -120) calidadSenal = 'Muy débil';
          else if (rssi < -110) calidadSenal = 'Débil';
          else if (rssi < -100) calidadSenal = 'Moderada';
          else if (rssi < -90) calidadSenal = 'Buena';

          return `<div style="min-width: 220px;">
            <div style="margin-bottom: 8px; font-weight: bold; font-size: 13px; color: #fff;">Uplink</div>
            <div style="display: flex; align-items: center; margin-bottom: 6px;">
              <span style="font-size: 12px; color: #ccc;">Timestamp:</span>
              <span style="font-size: 12px; color: #fff; margin-left: 6px; font-weight: 600;">${fechaStr}</span>
            </div>
            <div style="display: flex; align-items: center; margin-bottom: 6px;">
              <span style="display: inline-block; width: 10px; height: 10px; background-color: ${colorHex}; border-radius: 2px; margin-right: 8px;"></span>
              <span style="font-size: 12px; color: #ccc;">RSSI:</span>
              <span style="font-size: 14px; color: #fff; margin-left: 6px; font-weight: bold;">${rssi} dBm</span>
            </div>
            <div style="display: flex; align-items: center; margin-bottom: 6px;">
              <span style="font-size: 12px; color: #ccc;">Calidad:</span>
              <span style="font-size: 12px; color: #fff; margin-left: 6px; font-weight: 600;">${calidadSenal}</span>
            </div>
            <div style="display: flex; align-items: center; padding-top: 8px; border-top: 1px solid rgba(255, 255, 255, 0.2);">
              <span style="display: inline-block; width: 10px; height: 10px; background-color: ${colorHex}; border-radius: 2px; margin-right: 8px;"></span>
              <span style="font-size: 12px; color: #ccc;">ToA:</span>
              <span style="font-size: 14px; color: ${colorHex}; margin-left: 6px; font-weight: bold;">${toa.toLocaleString('es-AR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} ms</span>
            </div>
          </div>`;
        },
      },
    };
  }
}
